import {produce} from 'immer';
import areEqual from 'react-fast-compare';
import {applyNodeChanges, applyEdgeChanges, NodeChange, EdgeChange} from 'reactflow';

import {omit} from 'util/omitKey';

import {INTERACTION_FIELDS} from './processFlowchartConfigEditorConstants';
import {FlowchartEdge} from './processFlowchartConfigEditorConstants';
import {FlowchartNode, StaticFlowchartNodeData} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChange, FlowchartEdgeChange} from './processFlowchartConfigEditorUtils';

/**
 * Extracts the static data from the data object of a flowchart node.
 * @param nodeData The data object on a FlowchartNode.
 * @returns The static data of the node.
 */
export function extractStaticData(nodeData: FlowchartNode['data']): StaticFlowchartNodeData {
  return omit(
    nodeData,
    ...(INTERACTION_FIELDS as unknown as Array<keyof FlowchartNode['data']>)
  ) as StaticFlowchartNodeData;
}

export interface FlowchartChangeResult {
  updatedNodes: Array<FlowchartNode>;
  updatedEdges: Array<FlowchartEdge>;

  /**
   * Whether or not the static data of any nodes has changed.
   * This includes whether or not any nodes were added or removed.
   */
  hasNodeDataChanges: boolean;

  /**
   * Whether or not the structure of the flowchart has changed.
   * This includes whether or not any edges were added or removed.
   */
  hasStructuralChanges: boolean;
}

/**
 * Processes changes to a flowchart and returns the updated nodes and edges.
 *
 * @param nodeChanges - The changes to the nodes in the flowchart.
 * @param edgeChanges - The changes to the edges in the flowchart.
 * @param nodes - The nodes in the flowchart.
 * @param edges - The edges in the flowchart.
 * @returns An object containing the new nodes and edges as well as booleans describing whether or not these
 *   changes constitute an update to the static data of the flowchart or the structure of the flowchart.
 */
export default function processInteractiveFlowchartChanges({
  nodeChanges,
  edgeChanges,
  nodes,
  edges,
}: {
  nodeChanges: Array<FlowchartNodeChange>;
  edgeChanges: Array<FlowchartEdgeChange>;
  nodes: Array<FlowchartNode>;
  edges: Array<FlowchartEdge>;
}): FlowchartChangeResult {
  const reactFlowNodeChanges = nodeChanges.filter(
    (nodeChange): nodeChange is NodeChange => nodeChange.type !== 'replace'
  );
  const nodesWithReactFlowChanges = applyNodeChanges(reactFlowNodeChanges, nodes);
  const updatedNodes = produce(nodesWithReactFlowChanges, (draft) => {
    for (const nodeChange of nodeChanges) {
      if (nodeChange.type === 'replace') {
        const updatedNode = draft.find((node) => node.id === nodeChange.id);
        if (updatedNode != null) {
          updatedNode.data = nodeChange.data;
        }
      }
    }
  });

  // Only update static flowchart data if the static data has actually changed
  const hasNodeDataChanges =
    updatedNodes.length !== nodes.length ||
    updatedNodes.some((updatedNode) => {
      const originalNode = nodes.find((node) => node.id === updatedNode.id);
      if (!originalNode) {
        return true; // New node
      }

      const originalStaticData = extractStaticData(originalNode.data);
      const updatedStaticData = extractStaticData(updatedNode.data);
      return !areEqual(originalStaticData, updatedStaticData);
    });

  const reactFlowEdgeChanges = edgeChanges.filter(
    (edgeChange): edgeChange is EdgeChange => edgeChange.type !== 'label'
  );
  const reactFlowEdgesWithChanges = applyEdgeChanges(reactFlowEdgeChanges, edges) as Array<FlowchartEdge>;
  const updatedEdges = produce(reactFlowEdgesWithChanges, (draft) => {
    for (const edgeChange of edgeChanges) {
      if (edgeChange.type === 'label') {
        const updatedEdge = draft.find((edge) => edge.id === edgeChange.id);
        if (updatedEdge != null) {
          updatedEdge.label = edgeChange.label;
        }
      }
    }
  }).filter((edge) => {
    // Remove edges for nodes that were removed from the flowchart
    const sourceNode = updatedNodes.find((node) => node.id === edge.source);
    const targetNode = updatedNodes.find((node) => node.id === edge.target);
    return sourceNode != null && targetNode != null;
  });

  // Only update static flowchart data if edges were added or removed
  const currentEdgeIds = new Set(edges.map((edge) => edge.id));
  const updatedEdgeIds = new Set(updatedEdges.map((edge) => edge.id));

  // Check if any edges were added or removed
  const hasStructuralChanges =
    currentEdgeIds.size !== updatedEdgeIds.size ||
    [...currentEdgeIds].some((id) => !updatedEdgeIds.has(id)) ||
    [...updatedEdgeIds].some((id) => !currentEdgeIds.has(id));

  return {
    updatedNodes,
    updatedEdges,
    hasNodeDataChanges,
    hasStructuralChanges,
  };
}
